<template>
  <div>
    <div class="d-flex gap-2 align-items-center mb-3">
      <CompanySearchDropdown
        :model-value="companyId"
        placeholder="Select company"
        :with-label="false"
        :with-description="false"
        exclude-deleted
        @update:model-value="selectCompany"
      />
    </div>

    <div v-if="!companyId" class="text-muted">
      Select a company to view its ledger.
    </div>

    <LoadingIndicatorComponent v-else-if="isLoading" />

    <div v-else-if="overview">
      <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
        <h2 class="mb-0">
          {{ overview.company.name }}
          <span class="text-body-quaternary fw-medium"
            >({{ overview.company.id }})</span
          >
        </h2>
        <span class="badge badge-phoenix badge-phoenix-secondary"
          >Reads ·
          {{ overview.company.ledger_reads ? "ledger" : "legacy" }}</span
        >
      </div>

      <ul class="nav nav-pills gap-1 mb-4">
        <li v-for="tab in VIEWS" :key="tab.id" class="nav-item">
          <button
            type="button"
            class="nav-link py-1 px-3"
            :class="{ active: view === tab.id }"
            :data-test="`view-${tab.id}`"
            @click="view = tab.id"
          >
            {{ tab.label
            }}<span
              v-if="tab.id === 'entries'"
              class="ms-2 fw-normal opacity-75"
              >{{ entries.length }}</span
            >
          </button>
        </li>
      </ul>

      <LedgerOverview
        v-if="view === 'overview'"
        :overview
        :entries
        @show:entries="(patch) => changeFilters(patch, 'entries')"
      />
      <LedgerEntriesTable
        v-else
        :entries
        :filters
        @change:filters="(patch) => changeFilters(patch)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import CompanySearchDropdown from "@/components/common/CompanySearchDropdown.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { useQueryManager } from "@/composables/useQueryManager";
import LedgerEntriesTable from "./LedgerEntriesTable.vue";
import LedgerOverview from "./LedgerOverview.vue";
import { ledgerApi } from "./ledgerApi";
import type {
  LedgerEntry,
  LedgerEntryFilters,
  LedgerEntryState,
  LedgerOverview as LedgerOverviewData,
} from "./ledgerTypes";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

type LedgerView = "overview" | "entries";

const VIEWS: { id: LedgerView; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "entries", label: "Entries" },
];
const CLEARED_FILTERS: Record<string, undefined> = {
  "ledger-view": undefined,
  "ledger-kind": undefined,
  "ledger-state": undefined,
  "ledger-invoice": undefined,
  "ledger-entry": undefined,
};

const { globalErrorHandler } = useGlobalHandlers();
const { getQueryValue, getQueryValueWithDefault, parsers, updateQuery } =
  useQueryManager();

// `company_id` is shared with the Company detail tab, so switching tabs keeps the company.
const companyId = getQueryValue("company_id", parsers.number);
const view = getQueryValueWithDefault<LedgerView>(
  "ledger-view",
  parsers.enum<LedgerView>(["overview", "entries"]),
  "overview"
);
const kind = getQueryValue("ledger-kind", parsers.string);
const state = getQueryValue(
  "ledger-state",
  parsers.enum<LedgerEntryState>([
    "open",
    "held",
    "settled",
    "reversed",
    "none",
  ])
);
const invoiceId = getQueryValue("ledger-invoice", parsers.number);
const expandedEntryId = getQueryValue("ledger-entry", parsers.number);

const overview = ref<LedgerOverviewData | null>(null);
const entries = ref<LedgerEntry[]>([]);
const isLoading = ref(false);
let loadRequestId = 0;

const filters = computed<LedgerEntryFilters>(() => ({
  kind: kind.value,
  state: state.value,
  invoiceId: invoiceId.value,
  expandedEntryId: expandedEntryId.value,
}));

watch(companyId, load, { immediate: true });

// A function declaration, not a const: the immediate watcher above calls it before this line runs.
async function load(id: number | undefined) {
  overview.value = null;
  entries.value = [];
  const requestId = ++loadRequestId;
  if (id === undefined) return;
  isLoading.value = true;
  try {
    const [overviewResponse, entriesResponse] = await Promise.all([
      ledgerApi.getOverview(id),
      ledgerApi.listEntries(id),
    ]);
    // A slower answer from a superseded load must not overwrite the current one, even when the
    // selection has since come back to the same company.
    if (requestId !== loadRequestId) return;
    overview.value = overviewResponse.data;
    entries.value = entriesResponse.data.entries;
  } catch (error) {
    if (requestId === loadRequestId) globalErrorHandler(error);
  } finally {
    if (requestId === loadRequestId) isLoading.value = false;
  }
}

const selectCompany = (value: number | string | null) => {
  const id = typeof value === "string" ? parseInt(value, 10) : value;
  // One navigation: the previous company's filters and expanded entry don't apply to the next one.
  updateQuery({ ...CLEARED_FILTERS, company_id: id?.toString() });
};

// One navigation per change: separate query writes would clobber each other.
const changeFilters = (
  patch: Partial<LedgerEntryFilters>,
  nextView?: LedgerView
) => {
  updateQuery({
    ...(nextView ? { "ledger-view": nextView } : {}),
    ...("kind" in patch ? { "ledger-kind": patch.kind } : {}),
    ...("state" in patch ? { "ledger-state": patch.state } : {}),
    ...("invoiceId" in patch
      ? { "ledger-invoice": patch.invoiceId?.toString() }
      : {}),
    ...("expandedEntryId" in patch
      ? { "ledger-entry": patch.expandedEntryId?.toString() }
      : {}),
  });
};
</script>
